import { type ToolPostPayload, ToolType } from "@repo/app-types/entities/tool";
import { Size } from "@repo/ui/helpers/helpers";
import { CenteredModal } from "@repo/ui/Modal/centeredModal";
import { Modal } from "@repo/ui/Modal/modal";
import { ModalCloseButton } from "@repo/ui/Modal/modalCloseButton";
import { ModalHeader, modalHeader } from "@repo/ui/Modal/modalHeader";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import {
  IconCircleCheck,
  IconCode,
  IconPhone,
  IconPhoneEnd,
  IconSearch,
  IconTools,
} from "@tabler/icons-react";
import { type FC, useState } from "react";
import {
  Dialog,
  GridList,
  GridListItem,
  Header,
  Heading,
} from "react-aria-components";
import { useNavigate } from "react-router";
import { twMerge } from "tailwind-merge";
import { useCreateToolMutation } from "../../api/entities/tool.js";
import { getApiErrorMessage } from "../../api/helpers/errors.js";
import { appPaths } from "../../app/appPaths.js";
import { toolTypeTitles } from "../agents/components/toolTypeTitles.js";
import { useNavigateKeepCopilotChat } from "../copilot/copilotPageContext.js";
import {
  CreateToolModalDialog,
  type NewToolProposal,
} from "./createToolModalDialog.js";

export interface CreateToolModalProps {
  isOpen: boolean;
  /**
   * The proposal the tool is created from. The modal opens on the proposal's
   * tool type, without the type picker, and the created tool takes the id the
   * proposal minted.
   */
  proposal?: NewToolProposal;
  onClose: () => void;
}

type ToolTypeOption = {
  type: ToolType;
  description: string;
  Icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
};

const toolTypeOptions: ToolTypeOption[] = [
  {
    type: ToolType.CALL_TRANSFER,
    description: "Transfer the call to a human agent or phone number",
    Icon: IconPhone,
    iconColor: "bg-blue-500",
  },
  {
    type: ToolType.CALL_END,
    description: "Hang up or return the call to your contact center",
    Icon: IconPhoneEnd,
    iconColor: "bg-red-500",
  },
  {
    type: ToolType.CUSTOM,
    description: "Make API calls to any external service",
    Icon: IconTools,
    iconColor: "bg-purple-500",
  },
  {
    type: ToolType.CODE,
    description: "Run your own JavaScript to call one or more APIs",
    Icon: IconCode,
    iconColor: "bg-amber-500",
  },
  {
    type: ToolType.WEB_DOCUMENT_RESEARCH,
    description: "Lookup information in a set of PDF documents on the web.",
    Icon: IconSearch,
    iconColor: "bg-emerald-500",
  },
];

export const CreateToolModal: FC<CreateToolModalProps> = (props) => {
  const { isOpen, proposal, onClose } = props;
  const navigate = useNavigate();
  const navigateKeepCopilotChat = useNavigateKeepCopilotChat();

  const [selectedType, setSelectedType] = useState<ToolType | null>(null);
  const toolType = proposal?.data.tool_type ?? selectedType;

  const [createTool] = useCreateToolMutation();

  const handleClose = () => {
    setSelectedType(null);
    onClose();
  };

  const handleTypeSelect = (type: ToolType) => {
    setSelectedType(type);
  };

  const handleCreate = async (payload: ToolPostPayload) => {
    try {
      const created = await createTool({
        ...payload,
        ...(proposal ? { proposal_id: proposal.id } : {}),
      }).unwrap();

      showToast({
        variant: ToastVariant.Success,
        title: "Tool created",
        description: `${created.name} has been created successfully.`,
      });

      handleClose();
      const toolLocation = appPaths.agentTool.build({ toolId: created.id });
      // A tool created from a suggestion keeps the chat that suggested it.
      if (proposal) {
        navigateKeepCopilotChat(toolLocation);
      } else {
        navigate(toolLocation);
      }
    } catch (error) {
      showToast({
        variant: ToastVariant.Error,
        title: "Failed to create tool",
        description: getApiErrorMessage(error, "Please try again."),
      });

      // Rethrow so the form keeps what the user filled in
      throw error;
    }
  };

  // Show type picker
  if (!toolType) {
    return (
      <CenteredModal
        isOpen={isOpen}
        onOpenChange={(isOpen) => !isOpen && handleClose()}
        isDismissable
        size={Size.SMALL}
      >
        <Dialog>
          {() => (
            <>
              <Header
                className={modalHeader().header({
                  className: "px-4 pt-4 sm:px-6 sm:pt-6",
                })}
              >
                <Heading slot="title" className={modalHeader().title()}>
                  Create tool
                </Heading>
                <ModalCloseButton />
              </Header>

              <div className="flex flex-col p-4 sm:p-6">
                <GridList
                  className="flex flex-col gap-3"
                  aria-label="Tool type"
                  layout="stack"
                >
                  {toolTypeOptions.map((option) => {
                    const { Icon, iconColor, description } = option;
                    const title = toolTypeTitles[option.type];

                    return (
                      <GridListItem
                        key={option.type}
                        textValue={option.type}
                        className="group relative flex cursor-pointer items-center gap-4 rounded-lg border border-border-strong bg-white p-4 text-foreground shadow-xs hover:bg-gray-50 focus:outline-hidden focus-visible:border-indigo-600 focus-visible:ring-2 focus-visible:ring-indigo-600"
                        onAction={() => {
                          handleTypeSelect(option.type);
                        }}
                      >
                        <div
                          className={twMerge(
                            "flex flex-none items-center justify-center rounded-lg p-2",
                            iconColor,
                          )}
                        >
                          <Icon className="size-6 shrink-0 text-white" />
                        </div>

                        <div className="flex flex-1 flex-col items-start gap-y-1">
                          <span className="text-sm font-semibold">{title}</span>
                          <span className="text-xs text-muted-foreground">
                            {description}
                          </span>
                        </div>

                        <IconCircleCheck className="invisible size-5 flex-none text-indigo-600 group-data-[selected]:visible" />
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute -inset-px rounded-lg border-2 border-transparent group-focus-visible:border group-data-[selected]:border-indigo-600"
                        />
                      </GridListItem>
                    );
                  })}
                </GridList>
              </div>
            </>
          )}
        </Dialog>
      </CenteredModal>
    );
  }

  return (
    <Modal isOpen={isOpen} onCancel={handleClose} size={Size.LARGE}>
      <ModalHeader onClose={handleClose}>
        Create {toolTypeTitles[toolType].toLowerCase()} tool
      </ModalHeader>
      <CreateToolModalDialog
        type={toolType}
        proposal={proposal}
        onSave={handleCreate}
      />
    </Modal>
  );
};
